import { useCallback, useRef } from 'react';
import type { InputHub } from '../input/InputHub';
import type { ControlMode } from '../save/settings';
import { HoldButton } from './HoldButton';
import { SteeringWheel } from './SteeringWheel';

interface TouchControlsProps {
  hub: InputHub;
  controlMode: ControlMode;
  vibrate: boolean;
}

/**
 * Bildschirmsteuerung. Gas und Bremse sitzen in jedem Modus rechts,
 * links wechselt zwischen Pfeiltasten und Lenkrad.
 */
export function TouchControls({ hub, controlMode, vibrate }: TouchControlsProps): JSX.Element {
  const leftPressed = useRef(false);
  const rightPressed = useRef(false);
  const updateButtonSteer = useCallback(() => {
    hub.touch.steer = (rightPressed.current ? 1 : 0) - (leftPressed.current ? 1 : 0);
  }, [hub]);
  const steerLeftDown = useCallback(() => {
    leftPressed.current = true;
    updateButtonSteer();
  }, [updateButtonSteer]);
  const steerRightDown = useCallback(() => {
    rightPressed.current = true;
    updateButtonSteer();
  }, [updateButtonSteer]);
  const steerLeftUp = useCallback(() => {
    leftPressed.current = false;
    updateButtonSteer();
  }, [updateButtonSteer]);
  const steerRightUp = useCallback(() => {
    rightPressed.current = false;
    updateButtonSteer();
  }, [updateButtonSteer]);

  const throttleDown = useCallback(() => {
    hub.touch.throttle = 1;
  }, [hub]);
  const throttleUp = useCallback(() => {
    hub.touch.throttle = 0;
  }, [hub]);
  const brakeDown = useCallback(() => {
    hub.touch.brake = 1;
  }, [hub]);
  const brakeUp = useCallback(() => {
    hub.touch.brake = 0;
  }, [hub]);
  const handbrakeDown = useCallback(() => {
    hub.touch.handbrake = true;
  }, [hub]);
  const handbrakeUp = useCallback(() => {
    hub.touch.handbrake = false;
  }, [hub]);
  const hornDown = useCallback(() => {
    hub.hornActive = true;
  }, [hub]);
  const hornUp = useCallback(() => {
    hub.hornActive = false;
  }, [hub]);

  const noop = useCallback(() => undefined, []);
  const cameraTap = useCallback(() => hub.requestCameraChange(), [hub]);
  const resetTap = useCallback(() => hub.requestReset(), [hub]);

  return (
    <>
      {controlMode === 'wheel' ? (
        <SteeringWheel hub={hub} vibrate={vibrate} />
      ) : (
        <div className="controls-left">
          <HoldButton
            className="steer-btn"
            ariaLabel="Nach links lenken"
            icon="◀"
            vibrate={vibrate}
            onDown={steerLeftDown}
            onUp={steerLeftUp}
          />
          <HoldButton
            className="steer-btn"
            ariaLabel="Nach rechts lenken"
            icon="▶"
            vibrate={vibrate}
            onDown={steerRightDown}
            onUp={steerRightUp}
          />
        </div>
      )}

      <div className="quick-actions">
          <HoldButton
            className="aux-btn"
            ariaLabel="Kamera wechseln"
            icon="🎥"
            vibrate={vibrate}
            onDown={cameraTap}
            onUp={noop}
          />
          <HoldButton
            className="aux-btn"
            ariaLabel="Hupe"
            icon="📣"
            vibrate={vibrate}
            onDown={hornDown}
            onUp={hornUp}
          />
          <HoldButton
            className="aux-btn"
            ariaLabel="Zur Straße zurücksetzen"
            icon="⌂"
            vibrate={vibrate}
            onDown={resetTap}
            onUp={noop}
          />
          <HoldButton
            className="aux-btn"
            ariaLabel="Handbremse"
            icon="🅿"
            vibrate={vibrate}
            onDown={handbrakeDown}
            onUp={handbrakeUp}
          />
      </div>
      <div className="controls-right">
        <HoldButton
          className="pedal btn-stop"
          ariaLabel="Bremse und Rueckwaerts"
          icon="⬇"
          label="STOPP"
          vibrate={vibrate}
          onDown={brakeDown}
          onUp={brakeUp}
        />
        <HoldButton
          className="pedal btn-go"
          ariaLabel="Gas"
          icon="⬆"
          label="GAS"
          vibrate={vibrate}
          onDown={throttleDown}
          onUp={throttleUp}
        />
      </div>
    </>
  );
}
